import type { LegalBlock, LegalDoc, LegalRun } from '../lib/legal';

/**
 * Renders a legal document (Privacy Policy / Terms of Service).
 *
 * RECOVERED PAGE. These lived only inside the compiled bundle (commit d95bf96)
 * and were destroyed by any rebuild. The copy now lives as structured data in
 * ../lib/legal.ts, generated by parsing the live pages.
 *
 * Inline formatting arrives as typed runs, so nothing here needs
 * dangerouslySetInnerHTML — the copy is rendered as real elements.
 */

function Runs({ runs }: { runs: readonly LegalRun[] }) {
  return (
    <>
      {runs.map((r, i) => {
        if (r.k === 'strong') {
          return (
            <strong key={i} className="font-semibold text-landing-deep">
              {r.v}
            </strong>
          );
        }
        if (r.k === 'link') {
          const external = /^https?:/.test(r.href);
          return (
            <a
              key={i}
              href={r.href}
              {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
              className="text-landing-accent underline hover:no-underline"
            >
              {r.v}
            </a>
          );
        }
        return <span key={i}>{r.v}</span>;
      })}
    </>
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.kind) {
    case 'h2':
      return (
        <h2 className="mb-3 mt-10 text-xl font-bold text-landing-deep lg:text-2xl">
          <Runs runs={block.runs} />
        </h2>
      );
    case 'h3':
      return (
        <h3 className="mb-2 mt-6 text-base font-semibold text-landing-deep lg:text-lg">
          <Runs runs={block.runs} />
        </h3>
      );
    case 'ul':
      return (
        <ul className="mb-4 list-disc space-y-1.5 pl-6">
          {block.items.map((item, i) => (
            <li key={i} className="leading-[1.7]">
              <Runs runs={item} />
            </li>
          ))}
        </ul>
      );
    case 'p':
    default:
      return (
        <p className="mb-4 leading-[1.7]">
          <Runs runs={block.runs} />
        </p>
      );
  }
}

export function LegalPage({ doc }: { doc: LegalDoc }) {
  return (
    <main className="w-full bg-white px-5 py-12 sm:px-6 md:px-8 lg:px-10 lg:py-16 xl:px-16">
      <article className="mx-auto max-w-[820px] text-[15px] text-landing-deep-muted lg:text-base">
        <a
          href="/"
          className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-landing-accent no-underline hover:underline"
        >
          <span aria-hidden="true">←</span> Back to home
        </a>

        <h1 className="mb-3 text-[clamp(28px,4vw,40px)] font-extrabold leading-[1.15] tracking-[-0.5px] text-landing-deep">
          {doc.title}
        </h1>
        <p className="mb-10 text-sm text-landing-deep-muted">{doc.meta}</p>

        {doc.blocks.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </article>
    </main>
  );
}
